import { NumberRangeResponse } from '../../generated/api/models.js';
import { TypeKeys } from '../actions/index.js';
import { updateActionStatePhase } from '../common/index.js';
import { ActionPhase } from '../common/storeUtils.js';
import { reduceCrudAction } from './reducerUtils.js';
import type { ResourcesState } from '../../common/types/states.js';
import type { ActionTypes, LoadNumberRangeAction } from '../actions/index.js';
import type { ActionState, ActionsHistory } from '../common/store.js';

export function resourcesReducer(
  state: (ResourcesState & ActionsHistory) | undefined,
  action: ActionTypes
): (ResourcesState & ActionsHistory) | null {
  if (typeof state === 'undefined') {
    return null;
  }

  switch (action.type) {
    case TypeKeys.LOAD_NUMBER_RANGE: {
      // Since there can be multiple ranges this is a bit more complex than ...reduceCrudAction can't handle
      if (state && state.actions) {
        const inProgressAction: LoadNumberRangeAction | undefined = state.actions.find(
          (actionState: ActionState) =>
            actionState.value.type === TypeKeys.LOAD_NUMBER_RANGE &&
            actionState.phase === ActionPhase.IN_PROGRESS &&
            (actionState.value as LoadNumberRangeAction).rangeId === action.rangeId
        ) as LoadNumberRangeAction | undefined;

        if (inProgressAction !== undefined) {
          return state;
        }
      }

      return {
        ...state,
        actions: [
          ...(state && state.actions ? state.actions : []),
          {
            phase: ActionPhase.IN_PROGRESS,
            value: {
              ...action,
              id: action.id,
            },
          },
        ],
        numberRanges: {
          ...(state && state.numberRanges ? state.numberRanges : undefined),
          [action.rangeId]: {
            ...(state && state.numberRanges ? state.numberRanges[action.rangeId] : undefined),
            loading: true,
          },
        },
      };
    }

    case TypeKeys.LOAD_NUMBER_RANGE_FAILED: {
      if (state && state.actions) {
        const updatedActions = state.actions.map((actionState: ActionState) => {
          if (
            actionState.value.type === TypeKeys.LOAD_NUMBER_RANGE &&
            (actionState.value as LoadNumberRangeAction).rangeId === action.rangeId
          ) {
            return {
              ...actionState,
              phase: ActionPhase.FAILED,
            };
          }
          return actionState;
        });

        return {
          ...state,
          actions: updatedActions,
          numberRanges: {
            ...state.numberRanges,
            [action.rangeId]: {
              ...(state.numberRanges ? state.numberRanges[action.rangeId] : undefined),
              errors: action.errors,
              loading: false,
            },
          },
        };
      }
      return state;
    }

    case TypeKeys.LOAD_NUMBER_RANGE_FULFILLED: {
      if (state && state.actions) {
        const updatedActions = state.actions.map((actionState: ActionState) => {
          if (
            actionState.value.type === TypeKeys.LOAD_NUMBER_RANGE &&
            (actionState.value as LoadNumberRangeAction).rangeId === action.rangeId
          ) {
            return {
              ...actionState,
              phase: ActionPhase.SUCCESS,
            };
          }
          return actionState;
        });

        const numbers = action.numbers.sort();

        return {
          ...state,
          actions: updatedActions,
          numberRanges: {
            ...state.numberRanges,
            [action.rangeId]: {
              allNumbers: numbers,
              displayedNumbers:
                action.numberCategory === NumberRangeResponse.NumberCategoryEnum.CORPORATE ||
                action.numberCategory === NumberRangeResponse.NumberCategoryEnum.REGIONAL
                  ? numbers.slice(0, 4)
                  : numbers,
              loading: false,
              numberCategory: action.numberCategory,
            },
          },
        };
      }
      return state;
    }

    case TypeKeys.RESET_ADDRESS_SEARCH_RESULT: {
      return {
        ...state,
        addressSearchResult: undefined,
      };
    }

    case TypeKeys.SEARCH_PUBLIC_PAGES: {
      return {
        ...state,
        ...reduceCrudAction(action, state),
        pageSearchInProgress: true,
      };
    }

    case TypeKeys.SEARCH_ADDRESS: {
      return {
        ...state,
        ...reduceCrudAction(action, state),
      };
    }

    case TypeKeys.SEARCH_ADDRESS_FAILED: {
      return {
        ...state,
        actions: updateActionStatePhase(TypeKeys.SEARCH_ADDRESS, state, ActionPhase.IN_PROGRESS, ActionPhase.FAILED),
        errors: action.errors,
      };
    }

    case TypeKeys.SEARCH_ADDRESS_FULFILLED: {
      return {
        ...state,
        actions: updateActionStatePhase(TypeKeys.SEARCH_ADDRESS, state, ActionPhase.IN_PROGRESS, ActionPhase.SUCCESS),
        addressSearchResult: action.result,
      };
    }

    case TypeKeys.RESET_SEARCH_PUBLIC_PAGES_RESULT: {
      return {
        ...state,
        pageSearchResult: undefined,
      };
    }

    case TypeKeys.SEARCH_PUBLIC_PAGES_FAILED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.SEARCH_PUBLIC_PAGES,
          state,
          ActionPhase.IN_PROGRESS,
          ActionPhase.FAILED
        ),
        pageSearchInProgress: false,
      };
    }

    case TypeKeys.SEARCH_PUBLIC_PAGES_FULFILLED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.SEARCH_PUBLIC_PAGES,
          state,
          ActionPhase.IN_PROGRESS,
          ActionPhase.FAILED
        ),
        pageSearchInProgress: false,
        pageSearchResult: {
          total: action.result.total,
          pageHits:
            action.start === 1
              ? action.result.pageHits
              : [...(state.pageSearchResult?.pageHits || []), ...action.result.pageHits],
          spelling: action.result.spelling,
          promotions: action.result.promotions,
        },
      };
    }

    case TypeKeys.RESET_ERRORS: {
      return {
        ...state,
        errors: undefined,
      };
    }

    default:
      return state;
  }
}
